import {
  BlockStatus,
  DEFAULT_PROJECTS,
  RecordType,
  defaultProjectId,
  generateDrafts,
  isDraftExpired,
  isLive,
  liveOnly,
  lwwWins,
  makeEnvelope,
  parseEnvelope,
  isHlcBeyondDrift,
  isZeroHlc,
  repeatLookbackDates,
  resolveSettings,
  settingId,
  SettingKey,
  systemHlc,
  tombstoneOf,
  zeroHlc,
  type AnyEnvelope,
  type DefaultProjectKey,
  type HlcClock,
  type ProjectRecord,
  type TimeBlockRecord,
} from '@wiztime/core';
import type { RepoWriter } from '../repo';
import { rehomeTemplateToDay, rehomeToDay } from './day-bounds';

/**
 * System operations: they run in their own transaction and are never undoable. Generated
 * records carry the zero-time hlc; merged records keep the hlc of their author.
 */

export interface SystemEnvironment {
  readonly clock: HlcClock;
  readonly deviceId: string;
  readonly nowMs: number;
  readonly tz: string;
}

/**
 * Generate the drafts of `date` from the templates and the recent confirmed blocks, and store
 * the ones whose id is absent. Idempotent; a cleared (deleted) draft is never generated again.
 * Nothing is generated for a date whose drafts would already be expired.
 */
export async function generateDraftsForDate(
  tx: RepoWriter,
  env: SystemEnvironment,
  date: string,
): Promise<TimeBlockRecord[]> {
  const settings = resolveSettings(await tx.listByType(RecordType.Setting));
  if (isDraftExpired(date, settings, env.nowMs)) return [];
  const lookback = repeatLookbackDates(date, settings);
  const [templates, projects, recent, existing] = await Promise.all([
    tx.listByType(RecordType.Template),
    tx.listByType(RecordType.Project),
    tx.listByTypeInDateRange(
      RecordType.TimeBlock,
      lookback[0] ?? date,
      lookback[lookback.length - 1] ?? date,
    ),
    tx.listByTypeAndDate(RecordType.TimeBlock, date, { includeDeleted: true }),
  ]);
  const drafts = generateDrafts({
    date,
    templates,
    recentConfirmedBlocks: recent,
    settings,
    existingBlocksForDate: existing,
    deviceId: env.deviceId,
    tz: env.tz,
    activeProjectIds: new Set(
      liveOnly(projects)
        .filter((project) => project.data.active)
        .map((project) => project.id),
    ),
  });
  const stored: TimeBlockRecord[] = [];
  for (const draft of drafts) {
    // The id may exist as a tombstone whose personal date is unknown (not in `existing`).
    if ((await tx.get(draft.id)) !== undefined) continue;
    await tx.put(draft);
    stored.push(draft);
  }
  return stored;
}

export type DefaultProjectNames = Readonly<Record<DefaultProjectKey, string>>;

/** Create the default projects (work, sleep, chores) under their deterministic ids if absent. */
export async function seedDefaults(
  tx: RepoWriter,
  env: SystemEnvironment,
  names: DefaultProjectNames,
): Promise<ProjectRecord[]> {
  const created: ProjectRecord[] = [];
  for (const spec of DEFAULT_PROJECTS) {
    const id = defaultProjectId(spec.key);
    if ((await tx.get(id)) !== undefined) continue;
    const record = makeEnvelope({
      id,
      type: RecordType.Project,
      hlc: zeroHlc(env.deviceId),
      deviceId: env.deviceId,
      data: {
        name: names[spec.key],
        color: spec.color,
        category: spec.category,
        active: true,
        sort: spec.sort,
      },
    });
    await tx.put(record);
    created.push(record);
  }
  return created;
}

export interface RejectedRecord {
  /** Position in the input list. */
  readonly index: number;
  readonly issues: readonly string[];
}

export interface MergeResult {
  /** Identifiers of the records that were inserted, replaced or re-homed (pieces of cut blocks included). */
  readonly changedIds: readonly string[];
  /** Input records that failed validation or lie beyond the clock drift limit, and were skipped. */
  readonly rejected: readonly RejectedRecord[];
}

const DRIFT_ISSUE = "hlc: more than 24 hours ahead of this device's clock";

/**
 * Merge records from another device or an import: each valid record replaces the stored one
 * only when its hlc is strictly newer (last writer wins). The clock advances past every hlc seen.
 *
 * A record whose hlc is more than `MAX_HLC_DRIFT_MS` ahead of `nowMs` is rejected: it would
 * outrank every later edit on every device. Merged blocks that lie outside the personal days of
 * the current day start are re-homed afterwards (see `rehomeToDay`).
 */
export interface MergeOptions {
  /**
   * Record kinds whose incoming records replace a stored record with the same identifier even when the
   * stored one is newer (the first sync of a device that joins an existing space: the space's settings win).
   * Within one batch the last incoming record of an identifier still wins by the usual rule.
   */
  readonly remoteWinsTypes?: readonly RecordType[];
  /**
   * Do not re-home blocks and templates after this merge. The first sync of a device that joins an existing
   * space merges page by page while the day start is still unknown; the caller then calls
   * `rehomeAllToDayStart` once with the final day start, so no cut piece of an intermediate day start exists.
   */
  readonly deferRehome?: boolean;
}

export async function mergeRecords(
  tx: RepoWriter,
  clock: HlcClock,
  incoming: readonly unknown[],
  nowMs: number,
  options: MergeOptions = {},
): Promise<MergeResult> {
  const remoteWins = new Set<RecordType>(options.remoteWinsTypes ?? []);
  const valid: AnyEnvelope[] = [];
  const rejected: RejectedRecord[] = [];
  incoming.forEach((candidate, index) => {
    const parsed = parseEnvelope(candidate);
    if (!parsed.success) rejected.push({ index, issues: parsed.issues });
    else if (isHlcBeyondDrift(parsed.data.hlc, nowMs)) rejected.push({ index, issues: [DRIFT_ISSUE] });
    else valid.push(parsed.data);
  });

  const stored = await tx.getMany(valid.map((record) => record.id));
  const current = new Map<string, AnyEnvelope>();
  valid.forEach((record, index) => {
    const existing = stored[index];
    if (existing !== undefined) current.set(record.id, existing);
  });
  const winners = new Map<string, AnyEnvelope>();
  for (const record of valid) {
    clock.receive(record.hlc);
    const existing = current.get(record.id);
    // `remoteWinsTypes`: the incoming record replaces the stored one whatever the timestamps say.
    const forced = remoteWins.has(record.type) && existing !== undefined && !winners.has(record.id);
    if (!forced && !lwwWins(record, existing)) continue;
    current.set(record.id, record);
    winners.set(record.id, record);
  }
  await tx.putMany([...winners.values()]);
  const rehomed = options.deferRehome === true ? [] : await rehomeMergedBlocks(tx, winners);
  return { changedIds: [...new Set([...winners.keys(), ...rehomed])], rejected };
}

/**
 * Re-home the blocks that lie outside the personal days of the current day start: the merged
 * blocks, and all stored blocks and daily-slot templates when the merge changed the day-start
 * setting. Returns the identifiers of the records written.
 */
async function rehomeMergedBlocks(
  tx: RepoWriter,
  winners: ReadonlyMap<string, AnyEnvelope>,
): Promise<string[]> {
  const dayStartChanged = winners.has(settingId(SettingKey.DayStartMin));
  const merged = [...winners.values()].filter((record) => record.type === RecordType.TimeBlock);
  if (!dayStartChanged && merged.length === 0) return [];
  return rehomeStored(tx, dayStartChanged ? null : (merged as TimeBlockRecord[]), dayStartChanged);
}

/**
 * Re-home `blocks` (all stored blocks when null) and, when `templates` is true, all stored templates
 * under the day start the stored settings resolve to.
 */
async function rehomeStored(
  tx: RepoWriter,
  blocks: readonly TimeBlockRecord[] | null,
  templates: boolean,
): Promise<string[]> {
  const { dayStartMin } = resolveSettings(await tx.listByType(RecordType.Setting));
  const candidates = blocks ?? (await tx.listByType(RecordType.TimeBlock));

  const writes: AnyEnvelope[] = [];
  const extras: AnyEnvelope[] = [];
  for (const block of candidates) {
    const [first, ...rest] = rehomeToDay(block, dayStartMin) ?? [];
    if (first === undefined) continue;
    writes.push(first);
    extras.push(...rest);
  }
  if (templates) {
    for (const template of await tx.listByType(RecordType.Template)) {
      const [first, ...rest] = rehomeTemplateToDay(template, dayStartMin) ?? [];
      if (first === undefined) continue;
      writes.push(first);
      extras.push(...rest);
    }
  }
  // A later piece is written only when it is newer than what is stored under its identifier,
  // so a newer edit or deletion of the piece, made on any device, stays.
  const existing = await tx.getMany(extras.map((piece) => piece.id));
  extras.forEach((piece, index) => {
    if (lwwWins(piece, existing[index])) writes.push(piece);
  });
  await tx.putMany(writes);
  return writes.map((record) => record.id);
}

/**
 * Re-home every stored block and daily-slot template under the day start the stored settings resolve to
 * (the final step of the first sync that joined an existing space). Returns the identifiers written.
 */
export async function rehomeAllToDayStart(tx: RepoWriter): Promise<string[]> {
  return rehomeStored(tx, null, true);
}

/**
 * Reset every setting this device holds to its default: each setting record that has a real timestamp becomes
 * a tombstone with the system timestamp. It never leaves the device (system timestamps are not pushed), and
 * any record of the space, or later edit, outranks it. Nothing is re-homed: the day start that applies is
 * known only after the space delivered its settings. Returns the identifiers written.
 */
export async function resetSettingsToDefaults(tx: RepoWriter, deviceId: string): Promise<string[]> {
  const resets = (await tx.listByType(RecordType.Setting))
    .filter((record) => !record.deleted && !isZeroHlc(record.hlc))
    .map((record) => tombstoneOf(record, systemHlc(deviceId), deviceId));
  await tx.putMany(resets);
  return resets.map((record) => record.id);
}

/**
 * Delete the expired drafts of the given dates (they are invisible already); returns their ids.
 * The tombstones carry the system timestamp, so they never override a user's edit made on another
 * device (the same rule as `WriteSession.purge`).
 */
export async function purgeExpiredDrafts(
  tx: RepoWriter,
  env: SystemEnvironment,
  dates: readonly string[],
): Promise<string[]> {
  const settings = resolveSettings(await tx.listByType(RecordType.Setting));
  const purged: string[] = [];
  for (const date of dates) {
    if (!isDraftExpired(date, settings, env.nowMs)) continue;
    for (const block of await tx.listByTypeAndDate(RecordType.TimeBlock, date)) {
      if (!isLive(block) || block.data.status !== BlockStatus.Draft) continue;
      await tx.put(tombstoneOf(block, systemHlc(env.deviceId), env.deviceId));
      purged.push(block.id);
    }
  }
  return purged;
}
